/**
 * ============================================================================
 * ICS SERVICE - EXPORT & IMPORT
 * ============================================================================
 * Generiert und parst .ics Dateien für Calendar (Google Calendar, Outlook, Apple)
 *
 * Features:
 * - Einzelne Termine exportieren
 * - Mehrere Termine auf einmal
 * - Wiederholende Termine (RRULE)
 * - Erinnerungen (VALARM)
 * - RFC 5545 konform
 * - ICS Import/Parse
 *
 * @version 2.0.0 - Import Feature hinzugefügt
 */

class ICSService {
  
  /**
   * Exportiere einzelnen Termin
   */
  generateICS(appointment) {
    console.log('📤 Generating ICS for:', appointment.title);
    const ics = this.createICSContent([appointment]);
    this.downloadICS(ics, `termin-${appointment.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.ics`);
  }
  
  /**
   * Exportiere mehrere Termine
   */
  generateMultipleICS(appointments, filename = 'termine.ics') {
    console.log('📤 Generating ICS for', appointments.length, 'appointments');
    const ics = this.createICSContent(appointments);
    this.downloadICS(ics, filename);
  }
  
  /**
   * Erstelle ICS Content (RFC 5545)
   */
  createICSContent(appointments) {
    let ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//PLANIVIO//Calendar//DE',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'X-WR-CALNAME:PLANIVIO Termine',
      'X-WR-TIMEZONE:Europe/Berlin',
      'X-WR-CALDESC:Exportiert von PLANIVIO Haushalts-Management'
    ];
    
    appointments.forEach(apt => {
      const event = this.createEvent(apt);
      ics.push(...event);
    });
    
    ics.push('END:VCALENDAR');
    
    return ics.join('\r\n');
  }
  
  /**
   * Erstelle einzelnes VEVENT
   */
  createEvent(appointment) {
    const startDateTime = this.formatDateTime(appointment.date, appointment.time);
    const endDateTime = this.calculateEndTime(appointment.date, appointment.time, 1); // 1 Stunde Standard
    const now = this.formatDateTimeNow();
    
    const category = this.getCategoryName(appointment.category);
    const color = this.getCategoryColor(appointment.category);
    
    let event = [
      'BEGIN:VEVENT',
      `UID:homem-${appointment.id}@homem.app`,
      `DTSTAMP:${now}`,
      `DTSTART:${startDateTime}`,
      `DTEND:${endDateTime}`,
      `SUMMARY:${this.escapeText(appointment.title)}`,
    ];
    
    // Description
    if (appointment.description) {
      event.push(`DESCRIPTION:${this.escapeText(appointment.description)}`);
    }
    
    // Location
    if (appointment.location) {
      event.push(`LOCATION:${this.escapeText(appointment.location)}`);
    }
    
    // Categories
    event.push(`CATEGORIES:${category}`);
    
    // Color (für Clients die das unterstützen)
    event.push(`COLOR:${color}`);
    
    // Person
    if (appointment.person && appointment.person !== 'alle') {
      event.push(`ATTENDEE;CN=${this.escapeText(appointment.person)}:mailto:noreply@homem.app`);
    }
    
    // Status
    const status = appointment.status === 'completed' ? 'COMPLETED' : 
                   appointment.status === 'cancelled' ? 'CANCELLED' : 'CONFIRMED';
    event.push(`STATUS:${status}`);
    
    // Priority (basierend auf Kategorie)
    const priority = appointment.category === 'arzt' ? '1' : 
                     appointment.category === 'arbeit' ? '5' : '9';
    event.push(`PRIORITY:${priority}`);
    
    // Wiederholung
    if (appointment.recurring && appointment.recurring !== 'none') {
      const rrule = this.createRRule(appointment);
      if (rrule) {
        event.push(`RRULE:${rrule}`);
      }
    }
    
    // Erinnerung
    if (appointment.reminder_minutes) {
      const alarm = this.createAlarm(appointment);
      event.push(...alarm);
    }
    
    // Created/Modified timestamps
    event.push(`CREATED:${this.formatDateTimeISO(appointment.created_at || new Date())}`);
    event.push(`LAST-MODIFIED:${this.formatDateTimeISO(appointment.updated_at || new Date())}`);
    
    event.push('END:VEVENT');
    
    return event;
  }
  
  /**
   * Erstelle VALARM für Erinnerung
   */
  createAlarm(appointment) {
    const alarm = [
      'BEGIN:VALARM',
      'ACTION:DISPLAY',
      `DESCRIPTION:Erinnerung: ${this.escapeText(appointment.title)}`
    ];
    
    // Trigger: -PT{minutes}M
    alarm.push(`TRIGGER:-PT${appointment.reminder_minutes}M`);
    
    alarm.push('END:VALARM');
    
    return alarm;
  }
  
  /**
   * Erstelle RRULE für Wiederholung
   */
  createRRule(appointment) {
    let freq = '';
    switch (appointment.recurring) {
      case 'daily': freq = 'DAILY'; break;
      case 'weekly': freq = 'WEEKLY'; break;
      case 'monthly': freq = 'MONTHLY'; break;
      case 'yearly': freq = 'YEARLY'; break;
      default: return '';
    }
    
    let rrule = `FREQ=${freq}`;
    
    // UNTIL wenn recurring_until gesetzt
    if (appointment.recurring_until) {
      const until = this.formatDateTime(appointment.recurring_until, '23:59');
      rrule += `;UNTIL=${until}`;
    } else {
      // Default: 1 Jahr
      const oneYearLater = new Date(appointment.date);
      oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
      const until = this.formatDateTime(oneYearLater.toISOString().split('T')[0], '23:59');
      rrule += `;UNTIL=${until}`;
    }
    
    return rrule;
  }
  
  /**
   * Formatiere DateTime zu ICS Format: YYYYMMDDTHHMMSS
   */
  formatDateTime(dateStr, time = '00:00') {
    const d = new Date(dateStr);
    const [hours, minutes] = time.split(':');
    d.setHours(parseInt(hours || 0), parseInt(minutes || 0), 0, 0);
    
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hour = String(d.getHours()).padStart(2, '0');
    const minute = String(d.getMinutes()).padStart(2, '0');
    const second = '00';
    
    return `${year}${month}${day}T${hour}${minute}${second}`;
  }
  
  /**
   * Formatiere aktuelle Zeit
   */
  formatDateTimeNow() {
    return this.formatDateTimeISO(new Date());
  }
  
  /**
   * Formatiere Date Object zu ICS Format
   */
  formatDateTimeISO(date) {
    const d = typeof date === 'string' ? new Date(date) : date;
    
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hour = String(d.getHours()).padStart(2, '0');
    const minute = String(d.getMinutes()).padStart(2, '0');
    const second = String(d.getSeconds()).padStart(2, '0');
    
    return `${year}${month}${day}T${hour}${minute}${second}`;
  }
  
  /**
   * Berechne End-Zeit (Start + Dauer in Stunden)
   */
  calculateEndTime(dateStr, time, durationHours = 1) {
    const d = new Date(dateStr);
    const [hours, minutes] = time.split(':');
    d.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    
    // Add duration
    d.setHours(d.getHours() + durationHours);
    
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hour = String(d.getHours()).padStart(2, '0');
    const minute = String(d.getMinutes()).padStart(2, '0');
    
    return `${year}${month}${day}T${hour}${minute}00`;
  }
  
  /**
   * Get Category Name
   */
  getCategoryName(category) {
    const map = {
      'arzt': 'Arzttermin',
      'schule': 'Schule',
      'privat': 'Privat',
      'arbeit': 'Arbeit'
    };
    return map[category] || 'Termin';
  }
  
  /**
   * Get Category Color
   */
  getCategoryColor(category) {
    const map = {
      'arzt': '#f44336',
      'schule': '#2196f3',
      'privat': '#4caf50',
      'arbeit': '#ff9800'
    };
    return map[category] || '#9e9e9e';
  }
  
  /**
   * Escape Text für ICS Format (RFC 5545)
   */
  escapeText(text) {
    if (!text) return '';
    return text
      .replace(/\\/g, '\\\\')  // Backslash
      .replace(/;/g, '\\;')     // Semicolon
      .replace(/,/g, '\\,')     // Comma
      .replace(/\n/g, '\\n')    // Newline
      .replace(/\r/g, '');      // Carriage return entfernen
  }
  
  /**
   * Download ICS File
   */
  downloadICS(icsContent, filename) {
    console.log('📥 Downloading ICS file:', filename);
    
    // Create Blob with BOM for UTF-8
    const blob = new Blob(['\ufeff', icsContent], { 
      type: 'text/calendar;charset=utf-8' 
    });
    
    // Create download link
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }, 100);
    
    console.log('✅ ICS file downloaded successfully');
  }
  
  /**
   * Generate Share URL (optional - für zukünftige Implementierung)
   */
  generateShareURL(appointment) {
    // Base64 encode appointment data
    const data = btoa(JSON.stringify({
      title: appointment.title,
      date: appointment.date,
      time: appointment.time,
      description: appointment.description
    }));

    return `${window.location.origin}/calendar/import?data=${data}`;
  }

  // ============================================================================
  // ICS IMPORT FUNCTIONALITY
  // ============================================================================

  /**
   * Parse ICS File Content
   * @param {string} icsContent - Raw ICS file content
   * @returns {Array} Array of parsed appointments
   */
  parseICS(icsContent) {
    console.log('📥 Parsing ICS content...');

    const appointments = [];
    const lines = icsContent.replace(/\r\n /g, '').replace(/\r\n\t/g, '').split(/\r\n|\n|\r/);

    let currentEvent = null;
    let inEvent = false;
    let inAlarm = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (line === 'BEGIN:VEVENT') {
        inEvent = true;
        currentEvent = {
          title: '',
          description: '',
          date: '',
          time: '00:00',
          end_time: null,
          location: '',
          category: 'privat',
          person: 'alle',
          recurring: 'none',
          recurring_until: null,
          reminder_minutes: null,
          all_day: false,
          uid: ''
        };
      } else if (line === 'END:VEVENT') {
        if (currentEvent && currentEvent.title && currentEvent.date) {
          appointments.push(currentEvent);
        }
        currentEvent = null;
        inEvent = false;
      } else if (line === 'BEGIN:VALARM') {
        inAlarm = true;
      } else if (line === 'END:VALARM') {
        inAlarm = false;
      } else if (inEvent && !inAlarm && currentEvent) {
        this.parseEventLine(line, currentEvent);
      } else if (inAlarm && currentEvent) {
        this.parseAlarmLine(line, currentEvent);
      }
    }

    console.log(`📥 Parsed ${appointments.length} events from ICS`);
    return appointments;
  }

  /**
   * Parse a single VEVENT line
   */
  parseEventLine(line, event) {
    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) return;

    let key = line.substring(0, colonIndex);
    let value = line.substring(colonIndex + 1);

    // Handle parameters (e.g., DTSTART;VALUE=DATE:20240115)
    const semicolonIndex = key.indexOf(';');
    let params = {};
    if (semicolonIndex !== -1) {
      const paramStr = key.substring(semicolonIndex + 1);
      key = key.substring(0, semicolonIndex);
      paramStr.split(';').forEach(p => {
        const [pk, pv] = p.split('=');
        params[pk] = pv;
      });
    }

    switch (key) {
      case 'SUMMARY':
        event.title = this.unescapeText(value);
        break;

      case 'DESCRIPTION':
        event.description = this.unescapeText(value);
        break;

      case 'DTSTART':
        const startResult = this.parseICSDateTime(value, params);
        event.date = startResult.date;
        event.time = startResult.time;
        event.all_day = startResult.allDay;
        break;

      case 'DTEND':
        const endResult = this.parseICSDateTime(value, params);
        event.end_time = endResult.time;
        break;

      case 'LOCATION':
        event.location = this.unescapeText(value);
        break;

      case 'CATEGORIES':
        event.category = this.mapCategory(value);
        break;

      case 'UID':
        event.uid = value;
        break;

      case 'RRULE':
        this.parseRRule(value, event);
        break;

      case 'ATTENDEE':
        // Extract CN parameter for person
        const cnMatch = line.match(/CN=([^;:]+)/i);
        if (cnMatch) {
          event.person = this.unescapeText(cnMatch[1]);
        }
        break;
    }
  }

  /**
   * Parse VALARM line
   */
  parseAlarmLine(line, event) {
    if (line.startsWith('TRIGGER:')) {
      const trigger = line.substring(8);
      // Parse -PT15M or -P1D formats
      const minuteMatch = trigger.match(/-PT?(\d+)M/i);
      const hourMatch = trigger.match(/-PT?(\d+)H/i);
      const dayMatch = trigger.match(/-P(\d+)D/i);

      if (minuteMatch) {
        event.reminder_minutes = parseInt(minuteMatch[1]);
      } else if (hourMatch) {
        event.reminder_minutes = parseInt(hourMatch[1]) * 60;
      } else if (dayMatch) {
        event.reminder_minutes = parseInt(dayMatch[1]) * 24 * 60;
      }
    }
  }

  /**
   * Parse ICS DateTime (YYYYMMDD or YYYYMMDDTHHMMSS)
   */
  parseICSDateTime(value, params = {}) {
    let date = '';
    let time = '00:00';
    let allDay = false;

    // Check if it's a date-only value
    if (params.VALUE === 'DATE' || value.length === 8) {
      // Date only: YYYYMMDD
      const year = value.substring(0, 4);
      const month = value.substring(4, 6);
      const day = value.substring(6, 8);
      date = `${year}-${month}-${day}`;
      allDay = true;
    } else {
      // DateTime: YYYYMMDDTHHMMSS or YYYYMMDDTHHMMSSZ (UTC)
      const isUTC = value.endsWith('Z');
      const dateTimePart = value.replace('Z', '');
      const year = parseInt(dateTimePart.substring(0, 4));
      const month = parseInt(dateTimePart.substring(4, 6)) - 1;
      const day = parseInt(dateTimePart.substring(6, 8));
      const hour = dateTimePart.length >= 13 ? parseInt(dateTimePart.substring(9, 11)) : 0;
      const minute = dateTimePart.length >= 13 ? parseInt(dateTimePart.substring(11, 13)) : 0;

      let d;
      if (isUTC) {
        // UTC → lokale Zeit des Browsers konvertieren
        d = new Date(Date.UTC(year, month, day, hour, minute, 0));
      } else {
        d = new Date(year, month, day, hour, minute, 0);
      }

      date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    }

    return { date, time, allDay };
  }

  /**
   * Parse RRULE
   */
  parseRRule(rrule, event) {
    const parts = rrule.split(';');
    const ruleMap = {};

    parts.forEach(part => {
      const [key, value] = part.split('=');
      ruleMap[key] = value;
    });

    // Map FREQ to our recurring format
    switch (ruleMap.FREQ) {
      case 'DAILY':
        event.recurring = 'daily';
        break;
      case 'WEEKLY':
        event.recurring = 'weekly';
        break;
      case 'MONTHLY':
        event.recurring = 'monthly';
        break;
      case 'YEARLY':
        event.recurring = 'yearly';
        break;
      default:
        event.recurring = 'none';
    }

    // Parse UNTIL
    if (ruleMap.UNTIL) {
      const untilResult = this.parseICSDateTime(ruleMap.UNTIL);
      event.recurring_until = untilResult.date;
    }
  }

  /**
   * Map external category names to internal categories
   */
  mapCategory(category) {
    const lowerCat = category.toLowerCase();

    if (lowerCat.includes('arzt') || lowerCat.includes('doctor') || lowerCat.includes('medical')) {
      return 'arzt';
    }
    if (lowerCat.includes('schule') || lowerCat.includes('school') || lowerCat.includes('education')) {
      return 'schule';
    }
    if (lowerCat.includes('arbeit') || lowerCat.includes('work') || lowerCat.includes('business')) {
      return 'arbeit';
    }
    if (lowerCat.includes('familie') || lowerCat.includes('family')) {
      return 'familie';
    }

    return 'privat';
  }

  /**
   * Unescape ICS text
   */
  unescapeText(text) {
    if (!text) return '';
    return text
      .replace(/\\n/g, '\n')
      .replace(/\\,/g, ',')
      .replace(/\\;/g, ';')
      .replace(/\\\\/g, '\\');
  }

  /**
   * Read and parse an ICS file from a File object
   * @param {File} file - The file object from input
   * @returns {Promise<Array>} Promise resolving to array of appointments
   */
  async readICSFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const content = e.target.result;
          const appointments = this.parseICS(content);
          resolve(appointments);
        } catch (error) {
          reject(new Error('Fehler beim Parsen der ICS-Datei: ' + error.message));
        }
      };

      reader.onerror = () => {
        reject(new Error('Fehler beim Lesen der Datei'));
      };

      reader.readAsText(file, 'UTF-8');
    });
  }

  /**
   * Validate parsed appointments
   * @param {Array} appointments - Array of parsed appointments
   * @returns {Object} { valid: Array, invalid: Array, errors: Array }
   */
  validateImport(appointments) {
    const valid = [];
    const invalid = [];
    const errors = [];

    appointments.forEach((apt, index) => {
      const aptErrors = [];

      if (!apt.title || apt.title.trim() === '') {
        aptErrors.push('Titel fehlt');
      }

      if (!apt.date || !/^\d{4}-\d{2}-\d{2}$/.test(apt.date)) {
        aptErrors.push('Ungültiges Datum');
      }

      if (apt.time && !/^\d{2}:\d{2}$/.test(apt.time)) {
        apt.time = '00:00'; // Fix invalid time
      }

      if (aptErrors.length > 0) {
        invalid.push({ ...apt, errors: aptErrors });
        errors.push(`Event ${index + 1}: ${aptErrors.join(', ')}`);
      } else {
        valid.push(apt);
      }
    });

    return { valid, invalid, errors };
  }
}

// ✅ FIXED: Create instance first, then export
const icsService = new ICSService();
export default icsService;